import {
  OrganizationEngagementPattern,
  TOrganizationReportDomainBreakdownDto,
  TWholenessObservation,
  TWholenessObservationEngagementSignals,
} from '@/types/Organization.types';

const formatRate = (rate: number): string => `${Math.round(rate ?? 0)}%`;

// ["A"] -> "A" · ["A","B"] -> "A and B" · ["A","B","C"] -> "A, B, and C"
const joinWithAnd = (names: string[]): string => {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

// When every name in the group ends with the same word (e.g. "Wellness"), show it only on the last:
// ["Spiritual Wellness", "Physical Wellness"] -> ["Spiritual", "Physical Wellness"]
const collapseCommonSuffix = (names: string[]): string[] => {
  if (names.length < 2) return names;
  const lastWord = (name: string) => name.trim().split(/\s+/).pop() ?? '';
  const suffix = lastWord(names[0]);
  if (!suffix || !names.every((name) => lastWord(name) === suffix)) return names;
  return names.map((name, i) => (i === names.length - 1 ? name : name.slice(0, name.lastIndexOf(suffix)).trimEnd()));
};

// Renders domain scores as bullet lines, grouping domains that share the same score
// ("•  Spiritual, Vocational, and Physical Wellness (all 40/100)").
const renderDomainBullets = (domains: TOrganizationReportDomainBreakdownDto[]): string => {
  const namesByScore = new Map<number, string[]>();
  domains.forEach((domain) => {
    const names = namesByScore.get(domain.score) ?? [];
    names.push(domain.domainName);
    namesByScore.set(domain.score, names);
  });

  return [...namesByScore.entries()]
    .map(([score, names]) => {
      const label = joinWithAnd(collapseCommonSuffix(names));
      return names.length > 1 ? `•  ${label} (all ${score}/100)` : `•  ${label} (${score}/100)`;
    })
    .join('\n');
};

// One narrative sentence per engagement pattern (thresholds live on the BO WholenessObservationService).
export const getEngagementNarrative = (signals: TWholenessObservationEngagementSignals): string => {
  const engagement = formatRate(signals.engagementRate);
  const signUp = formatRate(signals.signUpRate);

  switch (signals.pattern) {
    case OrganizationEngagementPattern.HIGH_ENGAGEMENT_LOW_SIGNUP:
      return `The organization shows high engagement rates (${engagement}) but low sign-up rates (${signUp}), suggesting potential for improved participation.`;
    case OrganizationEngagementPattern.HIGH_ENGAGEMENT_HIGH_SIGNUP:
      return `The organization shows strong engagement (${engagement}) and sign-up rates (${signUp}), reflecting healthy adoption across the team.`;
    case OrganizationEngagementPattern.LOW_ENGAGEMENT_HIGH_SIGNUP:
      return `The organization shows high sign-up rates (${signUp}) but low engagement (${engagement}), suggesting members aren't returning after signing up.`;
    case OrganizationEngagementPattern.LOW_ENGAGEMENT_LOW_SIGNUP:
      return `The organization shows low engagement (${engagement}) and low sign-up rates (${signUp}), indicating both onboarding and ongoing participation need attention.`;
    case OrganizationEngagementPattern.BALANCED:
    default:
      return `The organization shows moderate engagement (${engagement}) and sign-up rates (${signUp}), with room to strengthen both adoption and ongoing participation.`;
  }
};

/**
 * Builds the multi-paragraph "Wholeness Observation" narrative shown next to the Wholeness Ring,
 * from the backend-derived `wholenessObservation` block. Returns a pre-wrap string (paragraphs
 * separated by blank lines, domains as `•` bullets).
 */
export const buildWholenessObservationText = (
  organizationName: string | undefined,
  observation: TWholenessObservation,
): string => {
  const orgName = organizationName?.trim() || 'This organization';
  const paragraphs: string[] = [];

  if (observation.strengthDomains.length > 0) {
    paragraphs.push(
      `${orgName} demonstrates strong performance in\n${renderDomainBullets(observation.strengthDomains)}`,
    );
  }

  if (observation.opportunityDomains.length > 0) {
    paragraphs.push(
      `While showing opportunities for development across several domains including\n${renderDomainBullets(
        observation.opportunityDomains,
      )}`,
    );
  }

  paragraphs.push(getEngagementNarrative(observation.engagementSignals));

  return paragraphs.join('\n\n');
};
